import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useApiConfigSelector, useDeviceTypes } from "@hopper-b2b/utilities";
import { isEqual } from "lodash-es";
import { flightApiConfigStoreKey } from "../../../../../../reducers/types";
import type { IStoreState } from "../../../../../../reducers/types";
import type { IRerunPrediction } from "../../../../../shop/actions/actions";
import type { FilterType } from "../../../../../shop/v3/components/HorizontalFilterBar/types";
import {
  setAirlineFilter,
  setAirportFilter,
  setFareclassOptionFilter,
  setFlightNumberFilter,
  setMaxPriceFilter,
  setOutboundArrivalTimeRange,
  setOutboundDepartureTimeRange,
  setReturnArrivalTimeRange,
  setReturnDepartureTimeRange,
  setStopsOption,
} from "../../../../actions/actions";
import { initialFilterOptions } from "../../../../reducer";

import {
  DesktopFlightShopSearchFilter,
  MobileFlightShopSearchFilter,
} from "./components";
import type { FlightShopSearchFilterConnectorProps } from "./container";

export interface IFlightShopSearchFilterProps
  extends FlightShopSearchFilterConnectorProps {
  hideAirportFilter?: boolean;
  hideFareFilter?: boolean;
  filtersOpen?: boolean;
  isReturn?: boolean;
  setFiltersOpen?: (open: boolean) => void;
  setRerunPrediction: () => IRerunPrediction;
  selectedFilterType?: FilterType;
}

export const FlightShopSearchFilter = ({
  hideAirportFilter,
  hideFareFilter,
  flightShopFilters,
  filtersOpen,
  isReturn,
  setFiltersOpen,
  setRerunPrediction,
  viewedForecastProperties,
  selectedFilterType,
}: IFlightShopSearchFilterProps) => {
  const { matchesMobile } = useDeviceTypes();
  const dispatch = useDispatch();
  const apiConfig = useApiConfigSelector(flightApiConfigStoreKey);
  const { priceMax } = flightShopFilters;

  // Reset all filters to default values
  const resetAllFilters = useCallback(() => {
    dispatch(setAirlineFilter(initialFilterOptions.airlineFilter));
    dispatch(setStopsOption(initialFilterOptions.stopsOption, apiConfig));
    dispatch(setFlightNumberFilter(initialFilterOptions.flightNumberFilter));
    dispatch(
      setOutboundArrivalTimeRange(initialFilterOptions.outboundArrivalTimeRange)
    );
    dispatch(
      setOutboundDepartureTimeRange(
        initialFilterOptions.outboundDepartureTimeRange
      )
    );
    dispatch(
      setReturnDepartureTimeRange(initialFilterOptions.returnDepartureTimeRange)
    );
    dispatch(
      setReturnArrivalTimeRange(initialFilterOptions.returnArrivalTimeRange)
    );
    dispatch(setMaxPriceFilter(priceMax.value));
    dispatch(
      setFareclassOptionFilter({
        basic: false,
        standard: false,
        enhanced: false,
        premium: false,
        luxury: false,
      })
    );
    dispatch(setAirportFilter(initialFilterOptions.airportFilter));
  }, [dispatch, apiConfig, priceMax]);

  // Check if any filters are active
  const hasActiveFilters = useSelector((state: IStoreState) => {
    const {
      stopsOption,
      fareclassOptionFilter,
      airlineFilter,
      airportFilter,
      flightNumberFilter,
      outboundDepartureTimeRange,
      outboundArrivalTimeRange,
      returnDepartureTimeRange,
      returnArrivalTimeRange,
    } = state.flightSearch;

    return (
      stopsOption !== initialFilterOptions.stopsOption ||
      !isEqual(
        fareclassOptionFilter,
        initialFilterOptions.fareclassOptionFilter
      ) ||
      airlineFilter.length > 0 ||
      airportFilter.length > 0 ||
      flightNumberFilter.length > 0 ||
      !isEqual(
        outboundDepartureTimeRange,
        initialFilterOptions.outboundDepartureTimeRange
      ) ||
      !isEqual(
        outboundArrivalTimeRange,
        initialFilterOptions.outboundArrivalTimeRange
      ) ||
      !isEqual(
        returnDepartureTimeRange,
        initialFilterOptions.returnDepartureTimeRange
      ) ||
      !isEqual(
        returnArrivalTimeRange,
        initialFilterOptions.returnArrivalTimeRange
      )
    );
  });

  return matchesMobile ? (
    <MobileFlightShopSearchFilter
      flightShopFilters={flightShopFilters}
      hideAirportFilter={hideAirportFilter}
      hideFareClassFilter={hideFareFilter}
      filtersOpen={filtersOpen}
      setFiltersOpen={setFiltersOpen}
      isReturn={isReturn}
      setRerunPrediction={setRerunPrediction}
      viewedForecastProperties={viewedForecastProperties}
      selectedFilterType={selectedFilterType}
      resetAllFilters={resetAllFilters}
      hasActiveFilters={hasActiveFilters}
    />
  ) : (
    <DesktopFlightShopSearchFilter
      flightShopFilters={flightShopFilters}
      hideAirportFilter={hideAirportFilter}
      hideFareClassFilter={hideFareFilter}
    />
  );
};
